/**
 * Template Management Page
 * CRUD operations for BE Study templates
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Copy,
  FolderOpen,
  Globe,
  Calendar,
} from 'lucide-react';
import { useTemplates } from '@/hooks/useTemplates';
import { DataTable } from '@/components/be-study/common/DataTable';
import { DeleteConfirmationModal } from '@/components/be-study/modals/ConfirmationModal';
import { COUNTRIES, formatDate } from '@/constants/beStudyMockData';
import { ROUTES } from '@/constants/routes';

/**
 * Template Management Page Component
 */
export function TemplateManagement() {
  const navigate = useNavigate();
  const {
    templates,
    deleteTemplate,
    searchTemplates,
  } = useTemplates();

  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Filter templates
  const filteredTemplates = useMemo(() => {
    let result = templates;

    if (searchQuery) {
      result = searchTemplates(searchQuery);
    }

    if (countryFilter && countryFilter !== 'all') {
      result = result.filter(t => t.country === countryFilter);
    }

    return result;
  }, [templates, searchQuery, countryFilter, searchTemplates]);

  // Table columns
  const columns = [
    {
      accessorKey: 'name',
      header: 'Template Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="font-medium">{row.name}</p>
            <p className="text-xs text-muted-foreground">v{row.version}</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'country',
      header: 'Country',
      cell: ({ value }) => {
        const country = COUNTRIES.find(c => c.code === value);
        return (
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span>{country?.name || value}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'sections',
      header: 'Sections',
      cell: ({ value }) => (
        <Badge variant="secondary">{value?.length || 0} sections</Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ value }) => (
        <Badge variant={value === 'ACTIVE' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Updated',
      cell: ({ value }) => (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          {formatDate(value)}
        </div>
      ),
    },
  ];

  // Handlers
  const handleView = (template) => {
    navigate(`/admin/templates/${template.id}`);
  };

  const handleEdit = (template) => {
    navigate(`/admin/templates/${template.id}/edit`);
  };

  const handleDelete = (template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedTemplate) {
      deleteTemplate(selectedTemplate.id);
      setShowDeleteModal(false);
      setSelectedTemplate(null);
    }
  };

  const handleAddTemplate = () => {
    navigate(ROUTES.TEMPLATE_CREATE);
  };

  // Row actions
  const rowActions = (row) => (
    <>
      <DropdownMenuItem onClick={() => handleView(row)} data-testid={`view-template-${row.id}`}>
        <Eye className="h-4 w-4 mr-2" />
        View Details
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleEdit(row)} data-testid={`edit-template-${row.id}`}>
        <Edit className="h-4 w-4 mr-2" />
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Copy className="h-4 w-4 mr-2" />
        Duplicate
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem 
        onClick={() => handleDelete(row)}
        className="text-red-600"
        data-testid={`delete-template-${row.id}`}
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete
      </DropdownMenuItem>
    </>
  );

  return (
    <BaseLayout>
      <div className="space-y-6" data-testid="template-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Template Management</h1>
            <p className="text-muted-foreground">
              Create and manage BE study report templates
            </p>
          </div>
          <Button onClick={handleAddTemplate} data-testid="add-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Template
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-templates-input"
                />
              </div>
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="w-[180px]" data-testid="country-filter">
                  <Globe className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Templates Table */}
        <Card>
          <CardContent className="p-0">
            <DataTable
              data={filteredTemplates}
              columns={columns}
              rowActions={rowActions}
              searchKeys={['name', 'description', 'country']}
              emptyMessage="No templates found"
              onRowClick={handleView}
            />
          </CardContent>
        </Card>

        {/* Delete Modal */}
        <DeleteConfirmationModal
          open={showDeleteModal}
          onOpenChange={setShowDeleteModal}
          onConfirm={handleConfirmDelete}
          itemName={selectedTemplate?.name}
          itemType="template"
        />
      </div>
    </BaseLayout>
  );
}

export default TemplateManagement;
